import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  TextInput,
  Select,
  SimpleGrid,
  Card,
  Image,
  Badge,
  Pagination,
  ActionIcon,
  Skeleton,
  Center,
  Alert,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconSearch,
  IconAlertCircle,
  IconPhotoOff,
  IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useAppUser } from '../hooks/useAppUser';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  fetchIngredientSummaries,
  refreshIngredient,
} from '../services/adminIngredientService';
import {
  IngredientSummary,
  IngredientRecord,
  INGREDIENT_CATEGORY_LABELS,
  INGREDIENT_CATEGORY_ORDER,
} from '../types/ingredient';
import { PantryCategory } from '../types/pantry';

const PAGE_SIZE = 48;
const STORAGE_KEY = 'ingredient-browser-state';

type StatusFilter = 'all' | 'resolved' | 'unresolved' | 'not_ingredient' | 'degraded';
type CategoryFilter = PantryCategory | 'all';
type StapleFilter = 'all' | 'staple' | 'non_staple';

const loadSavedState = (): {
  search?: string;
  status?: StatusFilter;
  category?: CategoryFilter;
  staple?: StapleFilter;
  page?: number;
} => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null') || {};
  } catch {
    return {};
  }
};

const statusBadge = (s: IngredientSummary) => {
  if (s.status === 'not_ingredient')
    return <Badge size="xs" color="gray" variant="light">not ingredient</Badge>;
  if (s.status === 'unresolved')
    return <Badge size="xs" color="red" variant="light">unresolved</Badge>;
  return <Badge size="xs" color="teal" variant="light">resolved</Badge>;
};

// Collapse a full record back into a list summary (after an inline refresh),
// mirroring the backend's getIngredientSummaries projection.
const recordToSummary = (rec: IngredientRecord): IngredientSummary => {
  const t = rec.productTiers;
  const rep = t.mid ?? t.value ?? t.premium;
  return {
    name: rec.normalizedName,
    displayName: rec.displayNames?.[0],
    defaultUnit: rec.defaultUnit,
    status: rec.notAnIngredient
      ? 'not_ingredient'
      : t.value || t.mid || t.premium
        ? 'resolved'
        : 'unresolved',
    degraded: !!rec.lastRefreshDegradedAt,
    pricePerUnitMid: t.mid?.pricePerUnit ?? rep?.pricePerUnit,
    imageUrl: rep?.imageUrl,
    category: rec.category,
    isStaple: rec.isStaple,
  };
};

export const IngredientBrowserPage = () => {
  usePageTitle('Ingredients | Meals You Love');
  const { user, loading: userLoading } = useAppUser();
  const navigate = useNavigate();
  const isAdmin = !!(user?.isAdmin || user?._adminOverride);

  const [all, setAll] = useState<IngredientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const saved = useRef(loadSavedState());
  const [search, setSearch] = useState(saved.current.search ?? '');
  const [status, setStatus] = useState<StatusFilter>(saved.current.status ?? 'all');
  const [category, setCategory] = useState<CategoryFilter>(saved.current.category ?? 'all');
  const [staple, setStaple] = useState<StapleFilter>(saved.current.staple ?? 'all');
  const [page, setPage] = useState(saved.current.page ?? 1);
  const [refreshing, setRefreshing] = useState<Set<string>>(new Set());

  const handleRefresh = async (name: string) => {
    if (!user) return;
    setRefreshing((s) => new Set(s).add(name));
    try {
      const updated = await refreshIngredient(user, name);
      const summary = recordToSummary(updated);
      setAll((prev) => prev.map((r) => (r.name === name ? summary : r)));
      notifications.show({
        title: 'Refreshed',
        message: `"${name}" re-fetched from Kroger`,
        color: 'teal',
      });
    } catch (e: any) {
      notifications.show({
        title: 'Refresh failed',
        message: e.message || 'Failed to refresh ingredient',
        color: 'red',
      });
    } finally {
      setRefreshing((s) => {
        const n = new Set(s);
        n.delete(name);
        return n;
      });
    }
  };

  // Redirect non-admins once the profile has loaded.
  useEffect(() => {
    if (!userLoading && user && !isAdmin) navigate('/');
  }, [userLoading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    let cancelled = false;
    setLoading(true);
    fetchIngredientSummaries(user)
      .then((data) => {
        if (!cancelled) {
          setAll(data);
          setError(null);
        }
      })
      .catch((e) => !cancelled && setError(e.message || 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [user, isAdmin]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((r) => {
      if (
        q &&
        !r.name.toLowerCase().includes(q) &&
        !(r.displayName?.toLowerCase().includes(q))
      )
        return false;
      if (category !== 'all' && r.category !== category) return false;
      if (staple === 'staple' && !r.isStaple) return false;
      if (staple === 'non_staple' && r.isStaple) return false;
      if (status === 'all') return true;
      if (status === 'degraded') return r.degraded;
      return r.status === status;
    });
  }, [all, search, status, category, staple]);

  // Reset to page 1 when the filter changes — but not on the initial mount,
  // so a page restored from sessionStorage survives a back-navigation.
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    setPage(1);
  }, [search, status, category, staple]);

  // Persist filter + pagination so returning from a detail view restores it.
  useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ search, status, category, staple, page })
    );
  }, [search, status, category, staple, page]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group wrap="nowrap">
          <ActionIcon variant="subtle" size="lg" onClick={() => navigate('/admin')}>
            <IconChevronLeft size={20} />
          </ActionIcon>
          <Stack gap={0}>
            <Title order={2}>Ingredient Database</Title>
            <Text c="dimmed" size="sm">
              Browse Kroger-backed ingredient records — pricing, tiers, and refresh status
            </Text>
          </Stack>
        </Group>

        <Group>
          <TextInput
            flex={1}
            leftSection={<IconSearch size={16} />}
            placeholder="Search ingredients…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <Select
            w={170}
            value={category}
            onChange={(v) => setCategory((v as CategoryFilter) || 'all')}
            data={[
              { value: 'all', label: 'All categories' },
              ...INGREDIENT_CATEGORY_ORDER.map((c) => ({
                value: c,
                label: INGREDIENT_CATEGORY_LABELS[c],
              })),
            ]}
          />
          <Select
            w={150}
            value={staple}
            onChange={(v) => setStaple((v as StapleFilter) || 'all')}
            data={[
              { value: 'all', label: 'All items' },
              { value: 'staple', label: 'Staples only' },
              { value: 'non_staple', label: 'Non-staples' },
            ]}
          />
          <Select
            w={170}
            value={status}
            onChange={(v) => setStatus((v as StatusFilter) || 'all')}
            data={[
              { value: 'all', label: 'All statuses' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'unresolved', label: 'Unresolved' },
              { value: 'not_ingredient', label: 'Not ingredient' },
              { value: 'degraded', label: 'Degraded (stale)' },
            ]}
          />
        </Group>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}

        {loading ? (
          <>
            <Skeleton height={16} width={120} />
            <SimpleGrid cols={{ base: 2, xs: 3, sm: 4, md: 5, lg: 6 }} spacing="sm">
              {Array.from({ length: 18 }).map((_, i) => (
              <Card key={i} withBorder radius="md" padding="xs">
                <Card.Section>
                  <Skeleton height={120} radius={0} />
                </Card.Section>
                <Skeleton height={14} width="85%" mt={10} />
                <Skeleton height={12} width="50%" mt={8} />
                <Skeleton height={16} width={60} radius="xl" mt={10} />
              </Card>
              ))}
            </SimpleGrid>
          </>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              {filtered.length} ingredient{filtered.length === 1 ? '' : 's'}
              {search || status !== 'all' || category !== 'all' || staple !== 'all'
                ? ` (of ${all.length})`
                : ''}
            </Text>

            <SimpleGrid cols={{ base: 2, xs: 3, sm: 4, md: 5, lg: 6 }} spacing="sm">
              {pageRows.map((r) => (
                <Card
                  key={r.name}
                  withBorder
                  radius="md"
                  padding="xs"
                  pos="relative"
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate(`/admin/ingredients/${encodeURIComponent(r.name)}`)
                  }
                >
                  <Card.Section
                    style={{ background: 'var(--mantine-color-gray-0)' }}
                  >
                    {r.imageUrl ? (
                      <Image src={r.imageUrl} h={120} fit="contain" />
                    ) : (
                      <Center h={120}>
                        <IconPhotoOff size={28} color="var(--mantine-color-gray-5)" />
                      </Center>
                    )}
                  </Card.Section>

                  <Text
                    size="sm"
                    fw={500}
                    lineClamp={2}
                    mt={6}
                    title={r.name}
                  >
                    {r.displayName ?? r.name}
                  </Text>

                  <Text size="xs" c="dimmed" mt={4}>
                    {r.pricePerUnitMid !== undefined
                      ? `$${r.pricePerUnitMid.toFixed(2)}/${r.defaultUnit ?? 'unit'}`
                      : r.defaultUnit ?? '—'}
                  </Text>

                  <Group gap={4} mt={6} wrap="wrap">
                    {statusBadge(r)}
                    {r.degraded && (
                      <Badge size="xs" color="orange" variant="light">
                        degraded
                      </Badge>
                    )}
                    {r.category && (
                      <Badge size="xs" color="blue" variant="light">
                        {INGREDIENT_CATEGORY_LABELS[r.category]}
                      </Badge>
                    )}
                    {r.isStaple && (
                      <Badge size="xs" color="grape" variant="light">
                        staple
                      </Badge>
                    )}
                  </Group>

                  <ActionIcon
                    variant="default"
                    size="sm"
                    radius="xl"
                    pos="absolute"
                    top={8}
                    right={8}
                    loading={refreshing.has(r.name)}
                    title="Refresh from Kroger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefresh(r.name);
                    }}
                  >
                    <IconRefresh size={14} />
                  </ActionIcon>
                </Card>
              ))}
            </SimpleGrid>

            {pageRows.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                No ingredients match.
              </Text>
            )}

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination value={page} onChange={setPage} total={totalPages} />
              </Group>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
};
